import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { DotLottie } from '@lottiefiles/dotlottie-web';
import { useAuth } from '../../AuthContext';
import { getTransferablePreAuthData, clearPreAuthWizardData } from '../../services/preAuthWizardCache';
import { saveStudyState } from '../../services/studyPlanStore';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import InlineEmailVerification from '../auth/InlineEmailVerification';

interface Props {
    onComplete?: () => void;
}

const AccountCreation: React.FC<Props> = ({ onComplete }) => {
    const navigate = useNavigate();
    const { signInWithGoogle, signUpWithEmail, currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [fadeToDashboard, setFadeToDashboard] = useState(false);
    const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
    const dataTransferredRef = useRef(false);

    // Form State
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Verification State
    const [needsVerification, setNeedsVerification] = useState(false);

    const handleGoogleSignIn = async () => {
        setIsLoading(true);
        setIsGoogleLoading(true);
        setError(null);

        try {
            await signInWithGoogle();
            // Google auth is auto-verified, transfer data will trigger
        } catch (err) {
            console.error('Google sign-in error:', err);
            setError('Přihlášení se nezdařilo. Zkus to prosím znovu.');
            setIsLoading(false);
            setIsGoogleLoading(false);
        }
    };

    const handleEmailSignUp = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            // Using part of email as display name initially
            const displayName = email.split('@')[0];
            await signUpWithEmail(email, password, displayName);
            // After successful signup, AuthContext will set currentUser
            // The useEffect below will catch it and check verification
        } catch (err: any) {
            console.error('Email sign-up error:', err);
            // Czech error messages
            if (err.code === 'auth/email-already-in-use') {
                setError('Tento email je již registrován.');
            } else if (err.code === 'auth/weak-password') {
                setError('Heslo musí mít alespoň 6 znaků.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Neplatný email.');
            } else {
                setError('Registrace se nezdařila. Zkuste to prosím znovu.');
            }
            setIsLoading(false);
        }
    };

    // Handle animation completion
    const handleAnimationComplete = useCallback(() => {
        console.log('[AccountCreation] Firework animation completed');
        setFadeToDashboard(true);
        setTimeout(() => {
            sessionStorage.setItem('justFinishedOnboarding', 'true');
            navigate('/', { state: { onboardingCompleted: true } });
        }, 800);
    }, [navigate]);

    // Listen for animation completion
    useEffect(() => {
        if (!dotLottie) return;

        const handleComplete = () => {
            handleAnimationComplete();
        };

        dotLottie.addEventListener('complete', handleComplete);

        return () => {
            dotLottie.removeEventListener('complete', handleComplete);
        };
    }, [dotLottie, handleAnimationComplete]);

    // Handle post-login data transfer
    const executeDataTransfer = useCallback(async () => {
        if (currentUser && !dataTransferredRef.current && !isPlaying && currentUser.emailVerified) {
            dataTransferredRef.current = true;
            const preAuthData = getTransferablePreAuthData();

            if (preAuthData && preAuthData.studyPlan) {
                try {
                    console.log('[AccountCreation] Transferring pre-auth data to user:', currentUser.uid);

                    await saveStudyState(currentUser.uid, {
                        onboardingData: preAuthData.onboardingData as any,
                        studyPlan: preAuthData.studyPlan,
                        version: 1,
                    });

                    clearPreAuthWizardData();

                    // Play the firework animation!
                    console.log('[AccountCreation] Playing firework animation');
                    setIsLoading(false);
                    setIsPlaying(true);

                    if (dotLottie) {
                        dotLottie.play();
                    }
                } catch (err) {
                    console.error('Error transferring data:', err);
                    setError('Nepodařilo se uložit studijní plán. Zkus to prosím znovu.');
                    setIsLoading(false);
                    dataTransferredRef.current = false;
                }
            } else {
                navigate('/');
            }
        }
    }, [currentUser, isPlaying, dotLottie, navigate]);

    useEffect(() => {
        if (!currentUser) return;

        // Check if we need to show the verification screen
        const isGoogleUser = currentUser.providerData?.some(
            (provider) => provider.providerId === 'google.com'
        );

        if (!currentUser.emailVerified && !isGoogleUser) {
            // Pause the wizard and show verification
            setNeedsVerification(true);
            setIsLoading(false);
            return;
        }

        // If verified (either Google or verified email), proceed
        if (needsVerification && currentUser.emailVerified) {
            setNeedsVerification(false);
        }

        executeDataTransfer();
    }, [currentUser, executeDataTransfer, needsVerification]);

    const handleVerificationComplete = () => {
        setNeedsVerification(false);
        // executeDataTransfer will run automatically after state update due to the useEffect
    };

    return (
        <div className={`space-y-4 sm:space-y-8 animate-in fade-in slide-in-from-right-4 duration-300 max-w-md mx-auto transition-all duration-700 ${fadeToDashboard ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
            {/* Firework Animation - frozen before login, plays after */}
            <div className="flex flex-col items-center justify-center">
                <div className="w-32 h-32 sm:w-64 sm:h-64">
                    <DotLottieReact
                        src="/materials/fireworklaunch.json"
                        loop={false}
                        autoplay={false}
                        className="w-full h-full"
                        dotLottieRefCallback={setDotLottie}
                    />
                </div>
            </div>

            <div className="text-center">
                <h3 className="text-gray-900 dark:text-white font-bold text-2xl mb-2">
                    {needsVerification ? 'Téměř hotovo!' : 'Tvůj studijní plán je připraven!'}
                </h3>
                {error ? null : !needsVerification && (
                    <p className="text-gray-500 dark:text-gray-400">
                        Vytvoř si účet a odstartuj svou přípravu na práva.
                    </p>
                )}
            </div>

            {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
            )}

            {needsVerification && !isPlaying && (
                <InlineEmailVerification theme="light" onVerified={handleVerificationComplete} />
            )}

            {!needsVerification && !isPlaying && (
                <div className="space-y-4">
                    {/* Email/Password Signup Form */}
                    <form onSubmit={handleEmailSignUp} className="space-y-4">
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email"
                                className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 rounded-xl px-12 py-4 border-2 border-gray-200 dark:border-gray-700 focus:border-brand-orange focus:outline-none transition-colors"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Heslo"
                                className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 rounded-xl px-12 py-4 border-2 border-gray-200 dark:border-gray-700 focus:border-brand-orange focus:outline-none transition-colors"
                                required
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                disabled={isLoading}
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orange/90 text-white font-bold py-4 px-6 rounded-xl shadow-[0_4px_0_rgb(194,65,12)] active:shadow-none active:translate-y-[4px] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading && !isGoogleLoading ? (
                                <Loader2 className="animate-spin h-5 w-5" />
                            ) : (
                                'Vytvořit účet'
                            )}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="relative flex py-2 items-center">
                        <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
                        <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">nebo</span>
                        <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
                    </div>

                    <button
                        onClick={handleGoogleSignIn}
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 font-bold py-4 px-6 rounded-xl border-2 border-gray-300 border-b-[6px] transition-all active:border-b-2 active:translate-y-[4px] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isGoogleLoading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700" />
                        ) : (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                                <span>Pokračovat s Google</span>
                            </>
                        )}
                    </button>

                    <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                        Kliknutím na tlačítko výše souhlasíš s našimi{' '}
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a href="#" className="underline hover:text-gray-600">
                            Podmínkami použití
                        </a>{' '}
                        a{' '}
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a href="#" className="underline hover:text-gray-600">
                            Ochranou soukromí
                        </a>
                    </p>
                </div>
            )}

            {!needsVerification && !isPlaying && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                        Už máš účet?{' '}
                        <button
                            onClick={() => navigate('/login')}
                            className="text-brand-orange font-bold hover:underline"
                        >
                            Přihlásit se
                        </button>
                    </p>
                </div>
            )}
        </div>
    );
};

export default AccountCreation;
